import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import { Code as CodeIcon } from '@mui/icons-material';
import { computeHtmlDiff } from '../../services/analytics.service';

export default function HtmlDiffToolPage() {
  const [beforeHtml, setBeforeHtml] = useState('');
  const [afterHtml, setAfterHtml] = useState('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);
  const [diffHtml, setDiffHtml] = useState<string | null>(null);

  const onComputeDiff = async () => {
    setDiffLoading(true);
    setDiffError(null);
    setDiffHtml(null);
    try {
      const res = await computeHtmlDiff({ before_html: beforeHtml, after_html: afterHtml });
      setDiffHtml(res.data?.diff_html || '<em>No diff returned</em>');
    } catch (e: any) {
      setDiffError(e?.message || 'Failed to compute diff');
    } finally {
      setDiffLoading(false);
    }
  };

  const handleClear = () => {
    setBeforeHtml('');
    setAfterHtml('');
    setDiffHtml(null);
    setDiffError(null);
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <CodeIcon fontSize="large" color="primary" />
        <Box>
          <Typography variant="h5">HTML Diff Tool</Typography>
          <Typography variant="body2" color="text.secondary">
            Compare two HTML snippets and visualize the differences
          </Typography>
        </Box>
      </Stack>

      {/* Instructions */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            How to Use
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            1. Paste your original HTML in the "Before HTML" field
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            2. Paste your modified HTML in the "After HTML" field
          </Typography>
          <Typography variant="body2" color="text.secondary">
            3. Click "Compute Diff" to see the differences highlighted
          </Typography>
        </CardContent>
      </Card>

      {/* Input Fields */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            HTML Input
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Before HTML"
              value={beforeHtml}
              onChange={(e) => setBeforeHtml(e.target.value)}
              multiline
              minRows={12}
              fullWidth
              placeholder="<html>&#10;  <head>&#10;    <title>Before</title>&#10;  </head>&#10;  <body>&#10;    <h1>Original Content</h1>&#10;  </body>&#10;</html>"
              sx={{
                fontFamily: 'monospace',
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
            <TextField
              label="After HTML"
              value={afterHtml}
              onChange={(e) => setAfterHtml(e.target.value)}
              multiline
              minRows={12}
              fullWidth
              placeholder="<html>&#10;  <head>&#10;    <title>After</title>&#10;  </head>&#10;  <body>&#10;    <h1>Modified Content</h1>&#10;  </body>&#10;</html>"
              sx={{
                fontFamily: 'monospace',
                '& textarea': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Stack>

          <Stack direction="row" spacing={2} mt={2} alignItems="center">
            <Button
              variant="contained"
              onClick={onComputeDiff}
              disabled={diffLoading || (!beforeHtml && !afterHtml)}
              startIcon={diffLoading ? <CircularProgress size={16} /> : <CodeIcon />}
            >
              {diffLoading ? 'Computing...' : 'Compute Diff'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClear}
              disabled={diffLoading}
            >
              Clear All
            </Button>
            {diffError && <Alert severity="error" sx={{ flex: 1 }}>{diffError}</Alert>}
          </Stack>
        </CardContent>
      </Card>

      {/* Diff Result */}
      {diffHtml && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Diff Result
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary" paragraph>
              Red background = Removed content • Green background = Added content
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: 600,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                backgroundColor: '#f5f5f5',
              }}
            >
              {/* eslint-disable-next-line react/no-danger */}
              <div dangerouslySetInnerHTML={{ __html: diffHtml }} />
            </Paper>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
